/**
 * Every project this machine has, for the remote Projects view: the app's own list when it has
 * `projects.list`, the platform's record of the roots this device registered either way, and
 * `workspaces.list` as the live overlay (see projectsModel.ts for the merge).
 */

import type { ProjectRoot } from '@aioe/shared';
import { useMemo, useRef } from 'react';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { mergeMachineProjects, type MergeResult, type PlatformProject, type RegisteredWorkspace } from './projectsModel.ts';
import type { LiveProject, Workbench } from './useWorkbench.ts';

/** A project's roots are read again after this long; the list itself is read on every poll. */
const DETAIL_TTL_MS = 60_000;
/** At most this many project reads in flight at once. */
const CONCURRENCY = 6;

async function inBatches<T, R>(items: T[], n: number, fn: (t: T) => Promise<R>): Promise<R[]> {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += n) out.push(...(await Promise.all(items.slice(i, i + n).map(fn))));
  return out;
}

type DetailCache = Map<string, { at: number; roots: ProjectRoot[] }>;

/**
 * The platform's projects the person may see, each with its roots. With the app's list only its
 * projects need their roots read (for the remote and branch); without it every project is read
 * for the roots this device registered. A project's roots are kept for a minute between polls.
 */
async function readPlatform(cache: DetailCache, liveIds: string[] | undefined): Promise<PlatformProject[]> {
  const visible = await routes.projects();
  const wanted = liveIds ? visible.filter((p) => liveIds.includes(p.id)) : visible;
  const now = Date.now();
  const stale = wanted.filter((p) => (cache.get(p.id)?.at ?? 0) < now - DETAIL_TTL_MS);
  await inBatches(stale, CONCURRENCY, async (p) => {
    const roots = await routes
      .project(p.id)
      .then((d) => d.roots)
      .catch(() => cache.get(p.id)?.roots ?? []);
    cache.set(p.id, { at: now, roots });
  });
  return visible.map((p) => ({ id: p.id, name: p.name, roots: cache.get(p.id)?.roots ?? [] }));
}

export function useMachineProjects(w: Workbench): MergeResult & { loading: boolean; error?: string; liveList: boolean; platformLoaded: boolean } {
  // `projects.list` when the app has it. Always an object once read, so "the app has no such method" is told from "not read yet".
  const live = useLoad<{ projects?: LiveProject[] }>(
    async () => {
      if (!w.connected) return {};
      const r = await w.tryCall<{ projects?: LiveProject[] } | LiveProject[]>('projects.list');
      return { projects: Array.isArray(r) ? r : r?.projects };
    },
    [w.connected, w.deviceId],
    20_000,
  );
  const liveSettled = !w.connected || live.data !== undefined || live.error !== undefined;
  const liveIds = live.data?.projects?.map((p) => p.id).sort();

  const cache = useRef<DetailCache>(new Map());
  const platform = useLoad<PlatformProject[] | undefined>(() => (liveSettled ? readPlatform(cache.current, liveIds) : Promise.resolve(undefined)), [w.deviceId, liveSettled, liveIds?.join('\n') ?? ''], 60_000);

  const registered = w.device.data?.workspaces as RegisteredWorkspace[] | undefined;
  const merged = useMemo(
    () =>
      mergeMachineProjects({
        deviceId: w.deviceId,
        live: live.data?.projects,
        platform: platform.data,
        workspaces: w.workspaces.data,
        registered,
        openWorkspace: w.status.data?.workspace,
      }),
    [w.deviceId, live.data, platform.data, w.workspaces.data, registered, w.status.data?.workspace],
  );

  return {
    ...merged,
    loading: !liveSettled || (platform.data === undefined && platform.error === undefined),
    error: platform.error,
    liveList: !!live.data?.projects,
    platformLoaded: platform.data !== undefined,
  };
}
